import React from 'react';
import {useTranslation} from 'react-i18next';
import {calculateRugbyPotentialGains} from '@/utils/rugbyLogic.js';

// Saisie du prono MI-TEMPS au rugby : vainqueur (ou nul) + écart à la pause.
// Même registre que la saisie finale rugby, mais cadré "mi-temps".
const SelectionButton = ({active, onClick, logoUrl, label, odd}) => (
    <button
        onClick={onClick}
        className={`flex-1 flex flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border-2 transition-all ${active ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}
    >
        {logoUrl
            ? <img src={logoUrl} alt="" className="w-7 h-7 object-contain rounded-full"/>
            : <span className="text-xl leading-none">🤝</span>}
        <span className="text-xs font-bold text-gray-700 truncate max-w-[90px]">{label}</span>
        {odd && odd !== '-' && (
            <span className="text-[10px] font-black text-indigo-500">x{odd}</span>
        )}
    </button>
);

export const StepBettingHalfTimeRugby = ({
                                             rugbyBet,
                                             homeTeam,
                                             awayTeam,
                                             fixture,
                                             onBetChange,
                                             onValidate,
                                             onBack = null,
                                         }) => {
    const {t} = useTranslation();

    const getOdd = (type) => fixture?.odds?.find(o => o.marketType === type)?.value || '-';
    const isDraw = rugbyBet.selection === 'DRAW';

    // Rugby : la cote mi-temps réutilise les cotes full-time (fixture.odds) pour l'instant.
    const gain = rugbyBet.selection
        ? calculateRugbyPotentialGains(rugbyBet.selection, isDraw ? 0 : (rugbyBet.margin || 1), fixture?.odds)
        : null;

    const handleSelection = (sel) => {
        onBetChange('selection', sel);
        if (sel === 'DRAW') onBetChange('margin', 0);
        else if (!rugbyBet.margin || rugbyBet.margin < 1) onBetChange('margin', 1);
    };

    const changeMargin = (op) => {
        const current = rugbyBet.margin || 1;
        let next = op === 'plus' ? current + 1 : current - 1;
        if (next < 1) next = 1;
        onBetChange('margin', next);
    };

    return (
        <div className="mt-8 animate-fade-in pb-4">
            <div className="text-center mb-5">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-2 rounded-full bg-indigo-50 border border-indigo-100">
                    <span className="text-sm leading-none">⏱️</span>
                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                        {t('half_time_betting.badge', 'Prono mi-temps')}
                    </span>
                </div>
                <h2 className="text-xl font-black text-gray-800 leading-tight">
                    {t('half_time_betting.rugby_title', 'Qui mène à la pause ?')}
                </h2>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
                <div className="flex gap-2">
                    <SelectionButton
                        active={rugbyBet.selection === 'HOME'}
                        onClick={() => handleSelection('HOME')}
                        logoUrl={homeTeam?.logoUrl}
                        label={homeTeam?.shortName || homeTeam?.name}
                        odd={getOdd('HOME_WINNER')}
                    />
                    <SelectionButton
                        active={isDraw}
                        onClick={() => handleSelection('DRAW')}
                        label={t('betting.rugby.draw', 'Nul')}
                        odd={getOdd('DRAW')}
                    />
                    <SelectionButton
                        active={rugbyBet.selection === 'AWAY'}
                        onClick={() => handleSelection('AWAY')}
                        logoUrl={awayTeam?.logoUrl}
                        label={awayTeam?.shortName || awayTeam?.name}
                        odd={getOdd('AWAY_WINNER')}
                    />
                </div>

                {rugbyBet.selection && !isDraw && (
                    <div className="mt-5 pt-4 border-t border-gray-100">
                        <p className="text-center text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                            {t('half_time_betting.rugby_margin', "Écart d'avance à la pause")}
                        </p>
                        <div className="flex items-center justify-center gap-5">
                            <button
                                onClick={() => changeMargin('minus')}
                                disabled={(rugbyBet.margin || 1) <= 1}
                                className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-xl transition ${(rugbyBet.margin || 1) <= 1 ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-gray-100 text-gray-600 active:scale-90 hover:bg-gray-200'}`}
                            >
                                －
                            </button>
                            <span className="text-[2.5rem] w-16 text-center font-black leading-none text-gray-800">
                                {rugbyBet.margin || 1}
                            </span>
                            <button
                                onClick={() => changeMargin('plus')}
                                className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-xl transition bg-gray-100 text-gray-600 active:scale-90 hover:bg-gray-200"
                            >
                                ＋
                            </button>
                        </div>
                    </div>
                )}

                {/* Gain potentiel mi-temps (rugby) : version compacte. */}
                {rugbyBet.selection && gain && (
                    <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                            <span>{isDraw ? '🤝' : '🏆'}</span>
                            {isDraw
                                ? t('half_time_betting.gain_draw', 'Bon nul à la pause')
                                : t('half_time_betting.gain_win', 'Bon résultat à la pause')}
                            {!isDraw && gain.costPerError > 0 && (
                                <span className="text-rose-500 font-bold">-{gain.costPerError}/pt</span>
                            )}
                        </span>
                        <div className="text-right shrink-0">
                            <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                                {t('betting.gain_total', 'Gain potentiel')}
                            </span>
                            <span className="text-2xl font-black text-brand-dark leading-none">
                                +{gain.total}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <button
                onClick={onValidate}
                disabled={!rugbyBet.selection}
                className={`w-full py-4 font-bold rounded-xl text-lg shadow-lg transition-transform ${rugbyBet.selection ? 'bg-action-green text-white hover:brightness-110 active:scale-98' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
                {t('half_time_betting.validate', 'Valider mon prono mi-temps')}
            </button>

            {onBack && (
                <button
                    onClick={onBack}
                    className="w-full py-3 mt-1 text-gray-400 hover:text-gray-600 font-bold text-sm uppercase tracking-widest transition-colors"
                >
                    {t('half_time_betting.skip', 'Je ne joue pas la mi-temps')}
                </button>
            )}
        </div>
    );
};

export default StepBettingHalfTimeRugby;
